// Small colour helpers for the tenant brand colour (no dependency).

export const isHexColor = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);

export const hexToRgb = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const rgbToHex = ([r, g, b]) => `#${[r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;

/** Blend `hex` towards `toward` (0–1). */
export const mix = (hex, toward, amount) => {
    const a = hexToRgb(hex);
    const b = hexToRgb(toward);
    return rgbToHex(a.map((v, i) => v + (b[i] - v) * amount));
};

const channel = (v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
export const luminance = (hex) => { const [r, g, b] = hexToRgb(hex).map(channel); return 0.2126 * r + 0.7152 * g + 0.0722 * b; };

/** WCAG contrast ratio between two colours (1–21). */
export const contrast = (a, b) => {
    const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (l1 + 0.05) / (l2 + 0.05);
};

const WHITE = '#ffffff';
const INK = '#0f172a';

/** Text colour for content on a `bg` fill: white unless that's hard to read. */
export const onColor = (bg) => (contrast(WHITE, bg) >= 4.5 || contrast(WHITE, bg) >= contrast(INK, bg) ? WHITE : INK);

/**
 * Nudges `hex` lighter (dark page) or darker (light page) until it stands out
 * from `pageBg` by at least `min` (3:1 is the WCAG bar for UI elements).
 */
export const legibleOn = (hex, pageBg, min = 3) => {
    const toward = luminance(pageBg) < 0.5 ? WHITE : INK;
    let c = hex;
    for (let i = 0; i < 20 && contrast(c, pageBg) < min; i++) c = mix(c, toward, 0.08);
    return c;
};

// Page and card backgrounds of the two presets (see index.css).
const PRESET_BG = { turquoise: { page: '#0f172a', card: '#1e293b' }, peach: { page: '#fff8f3', card: '#ffffff' } };

/**
 * CSS custom properties that apply a brand colour on top of a preset.
 * Returned as { '--primary': '#…', … } so they can be cached and replayed
 * before first paint. The colour is adjusted only as far as needed so that:
 *  - text and links in it read at 4.5:1 on both page and card backgrounds;
 *  - button text on it (white or dark, whichever fits) reads at 4.5:1;
 *  - the hover shade moves away from the button text, never towards it.
 */
export const brandVars = (brand, preset) => {
    if (!isHexColor(brand)) return {};
    const { page, card } = PRESET_BG[preset] || PRESET_BG.turquoise;
    const dark = luminance(page) < 0.5;
    let primary = legibleOn(legibleOn(brand.toLowerCase(), page, 4.5), card, 4.5);
    let text = onColor(primary);
    for (let i = 0; i < 20 && contrast(text, primary) < 4.5; i++) {
        primary = mix(primary, text === WHITE ? INK : WHITE, 0.08); // away from the text colour
        text = onColor(primary);
    }
    const [r, g, b] = hexToRgb(primary);
    return {
        '--primary': primary,
        '--primary-hover': mix(primary, text === WHITE ? '#000000' : WHITE, 0.12), // darker under white text, lighter under dark
        '--secondary': mix(primary, dark ? WHITE : INK, 0.3),
        '--accent-subtle': `rgba(${r}, ${g}, ${b}, ${dark ? 0.12 : 0.08})`,
        '--card-border': `rgba(${r}, ${g}, ${b}, ${dark ? 0.16 : 0.2})`,
        '--shadow': `0 4px 24px rgba(${r}, ${g}, ${b}, 0.08)`,
        '--on-primary': text,
    };
};

export const BRAND_VAR_NAMES = ['--primary', '--primary-hover', '--secondary', '--accent-subtle', '--card-border', '--shadow', '--on-primary'];
